import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import * as applicationApi from '../../../api/application';
import * as AppConstants from '../../../app/AppConstants';

import { handleOlppPaymentCreate, handleCcpPaymentCreate } from '../../payment/Payment/PaymentSlice'

const initialState = {
  isLoading: true,
  applicationStage: AppConstants.APPLICATION_STAGE.SELECT_REASON,
  acked: false,
  ccpUrl: null,
  applicationInput: {
    netId: null,
    reason: null,
    additionalService: null,
    photo: null,
    paymentMethod: null
  },
  isPendingDialogOpen: false,
  isReminderDialogOpen: false
};

export const getPendingCount = createAsyncThunk(
  'userApplication/pending/search',
  async (payload, ThunkAPI) => {
    const { userCreate } = ThunkAPI.getState();
    
    const apiResponse = await applicationApi.getPendingCount({
      searchType: AppConstants.SEARCH_TYPE.SELF,
      netId: payload ?? userCreate.applicationInput.netId
    });
    return apiResponse.data?.data;
  }
);

export const setApplicationStage = createAsyncThunk(
  'userApplication/stage/set',
  async (next, ThunkAPI) => {
    const { userCreate } = ThunkAPI.getState();
    
    if (next === AppConstants.APPLICATION_STAGE.MAKE_PAYMENT) {
      ThunkAPI.dispatch(createApplication(userCreate.applicationInput));
    }

    return next;
  }
);

export const createApplication = createAsyncThunk(
  'userApplication/create',
  async (payload, ThunkAPI) => {
    const apiResponse = await applicationApi.create(payload);
    const application = apiResponse.data;

    const { app, userCreate } = ThunkAPI.getState();

    // proceed to olpp payment
    if (userCreate.applicationInput.paymentMethod === AppConstants.APPLICATION_PAYMENT_METHOD.OLPP.ID) {
      ThunkAPI.dispatch(handleOlppPaymentCreate({
        netId: app.me.user?.userId,
        applicationId: application.data?.applicationId,
      }));
    }
    // proceed to ccp payment
    else if (userCreate.applicationInput.paymentMethod === AppConstants.APPLICATION_PAYMENT_METHOD.CCP.ID) {
      ThunkAPI.dispatch(handleCcpPaymentCreate({
        netId: app.me.user?.userId,
        applicationId: application.data?.applicationId
      }));
    }

    return application;
  }
);

export const userCreateSlice = createSlice(
  {
    name: 'userCreate',
    initialState,
    reducers: {
      resetStore: () => initialState,
      setAcked: (state, action) => {
        state.acked = action.payload;
      },
      setCCPUrl: (state, action) => {
        state.ccpUrl = action.payload;
      },
      dispatchApplicationInput: (state, action) => {
        state.isLoading = true;
        state.acked = false;

        for (const applicationInput of action.payload) {
          state.applicationInput[applicationInput.name] = state.applicationInput[applicationInput.name] !== applicationInput.value
            ? applicationInput.value
            : null;

          if (applicationInput.name === 'reason'
            && applicationInput.value !== AppConstants.APPLICATION_REASON.INTACT.ID
            && state.applicationInput.additionalService == null) {
            state.applicationInput.additionalService = AppConstants.APPLICATION_ADDITIONAL.NONE.ID
          }
        }

        if (state.applicationInput.reason == null
          || state.applicationInput.reason === AppConstants.APPLICATION_REASON.INTACT.ID
          && state.applicationInput.additionalService === AppConstants.APPLICATION_ADDITIONAL.NONE.ID) {
          state.applicationInput.additionalService = null;
        }

        if (state.applicationInput.additionalService !== AppConstants.APPLICATION_ADDITIONAL.PHOTO_CHANGED.ID) {
          state.applicationInput.photo = null;
        }

        state.isLoading = false;
      },
      setIsPendingDialogOpen: (state, action) => {
        state.isPendingDialogOpen = action.payload;
      },
      setIsReminderDialogOpen: (state, action) => {
        state.isReminderDialogOpen = action.payload;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(getPendingCount.fulfilled, (state, action) => {
          state.isLoading = false;
          const count = action.payload;
          state.isPendingDialogOpen = !!count && count > 0;
          state.isReminderDialogOpen = !count || count === 0;
        })
        .addCase(setApplicationStage.fulfilled, (state, action) => {
          const next = action.payload;

          if (next === AppConstants.APPLICATION_STAGE.SELECT_REASON) {
            state.acked = false;
            state.ccpUrl = null;
            state.applicationInput.paymentMethod = null;
          }
          
          if (next !== AppConstants.APPLICATION_STAGE.MAKE_PAYMENT) {
            state.applicationStage = next;
          }
        })
        .addCase(createApplication.fulfilled, (state, action) => {
          state.isLoading = false;

          if (state.applicationInput.paymentMethod === AppConstants.APPLICATION_PAYMENT_METHOD.CCP.ID) {
            state.applicationStage = AppConstants.APPLICATION_STAGE.COMPLETED_CCP;
          }
          else if (state.applicationInput.paymentMethod !== AppConstants.APPLICATION_PAYMENT_METHOD.OLPP.ID) {
            state.applicationStage = AppConstants.APPLICATION_STAGE.COMPLETED;
          }
        })
        .addCase(handleCcpPaymentCreate.fulfilled, (state, action) => {
          state.isLoading = false;
          const ccpUrl = action.payload;
          state.ccpUrl = ccpUrl;
          window.open(ccpUrl, '_blank').focus();
        })
        .addCase(handleOlppPaymentCreate.fulfilled, (state, action) => {
          state.isLoading = false;
          window.location.href = action.payload;
        })
        .addCase(handleOlppPaymentCreate.rejected, (state, action) => {
          state.isLoading = false;
          state.applicationStage = AppConstants.APPLICATION_STAGE.OLPP_FAILED;
        })
        .addMatcher(isAnyOf(
            getPendingCount.pending,
            createApplication.pending,
            handleCcpPaymentCreate.pending,
            handleOlppPaymentCreate.pending,
          ), (state, action) => {
          state.isLoading = true;
        })
        .addMatcher(isAnyOf(
            getPendingCount.rejected,
            createApplication.rejected,
            handleCcpPaymentCreate.rejected,
          ), (state, action) => {
          state.isLoading = false;
          state.isPendingDialogOpen = false;
          state.isReminderDialogOpen = false;
        })
    }
  }
);

export default userCreateSlice;